/**
 * A straight (non-detoured) conditional edge.
 *
 * Exists only so the label can be a DOM node instead of React Flow's built-in SVG
 * `<text>`: the label has to carry an order badge, a dead-branch mark, and the raw
 * expression as a `title` tooltip, none of which fit in an SVG text label. The path
 * is `getSmoothStepPath` — the same geometry `PLAIN_EDGE_TYPE = 'smoothstep'` gives
 * the unlabelled edges — so these draw exactly as the default ones they replace.
 * It was a bezier at first, which made labelled edges curve while every other edge in
 * the graph turned right angles, against both the mock and SkipEdge's own routing.
 *
 * Unconditional (`always`) and derived (`grant`) edges keep the default renderer —
 * they carry no label, so there is nothing to gain.
 */
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, type EdgeProps } from '@xyflow/react';

import type { CpaEdge } from '../../utils/projection';
import EdgeLabel from './EdgeLabel';

export default function ConditionEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  data,
}: EdgeProps) {
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  const edge = (data as { edge?: CpaEdge } | undefined)?.edge;

  return (
    <>
      <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd} />
      {edge && (
        <EdgeLabelRenderer>
          <div
            // nodrag/nopan: without them, grabbing the label pans the canvas.
            className="nodrag nopan cpa-edge-label-wrap"
            /*
             * Alternate rows by evaluation order, so adjacent branches of one fan-out
             * never sit on the same line. Narrowing the label stopped them overlapping;
             * staggering is what makes five of them readable at a glance instead of a
             * single dense strip.
             *
             * Keyed on `ruleIndex` (1-based) rather than on position, so the offset is
             * stable: the same branch lands on the same row every render.
             */
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${
                labelY + ((edge?.ruleIndex ?? 1) % 2 === 0 ? 11 : -11)
              }px)`,
            }}
          >
            <EdgeLabel edge={edge} />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
